import React, { FC, useState, useCallback } from "react";
import { FileWithPreview } from "react-dropzone";
import siteConfig from "site-config";
import DirectS3Uploader from "components/form/direct-s3-uploader";
import type { IScope } from "reducers/scope";
import { ContentLoading } from "components/content-loading";
import { IValidationProps, IUploaderProps, withMediaStyles } from "components/upload/dropzone-uploader";
import { useIsMounted } from "hooks";

// prettier-ignore
export interface IMediaUploaderProps extends IValidationProps, Omit<IUploaderProps, "onDrop"> {
    scope: IScope;
    style?: any;
    loading?: boolean;
    // rest props for s3 uploader
    multiple?: boolean;
    className?: string;
    inputProps?: { [key: string]: any };
    onUpload?: ((results: Array<[{ id: string }, FileWithPreview]>) => void) | ((response: { id: string }, file: FileWithPreview) => void); // multiple/single upload
    onUploadStart?(): void;
    onLoading?(loading: boolean): void;
    onFilesSelected?(files: File[], triggerUpload: () => void): void;
    onDrop?(files: File[]): void;
    maxSizeError?: string;
}

export const Uploader: FC<IMediaUploaderProps> = (props) => {
    const { onUpload, children, loading, onLoading, style, className, ...rest } = props;
    const [uploading, setUploading] = useState<boolean>(false);
    const isMounted = useIsMounted(); // prevent updating when processing async uploading status

    const handleLoading = useCallback(
        (flag: boolean) => {
            if (isMounted()) {
                setUploading(flag);
            }
            onLoading?.(flag);
        },
        [isMounted, onLoading],
    );

    const dropzoneStyles = withMediaStyles(style);
    return (
        <DirectS3Uploader
            uploadUrl={`${siteConfig.goAPI}/v1/uploads`}
            disabled={loading || uploading}
            {...dropzoneStyles}
            {...rest}
            className={className}
            uploadCallback={onUpload}
            onLoading={handleLoading}
        >
            {children}
            {(loading || uploading) && <ContentLoading />}
        </DirectS3Uploader>
    );
};

export default Uploader;
